import "./csv.scss";
import React, {useState, useContext} from "react";
import toast from "react-hot-toast";
import {
    Button,
    Card,
    Input,
    Typography,
    List,
    Space,
    Row,
    Col,
    Checkbox,
    Badge,
    Empty,
    Tooltip,
    Popconfirm,
    Select,
    Spin,
    Table,
    Slider,
    InputNumber,
    Segmented
} from "antd";
import {
    UploadOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FileTextOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import ScrollToTop from "../../shared/components/common/ScrollToTop";
import {useCSVList, useDeleteCSV, useGitlabBranchList, useUploadCSV, useUploadCSVLocal, useCsvMemo} from "./useCSVList";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {DarkModeContext} from "../../shared/contexts/darkModeContext";
import ServerRefresh from "../../shared/components/common/ServerRefresh";
import {AuthContext} from "../../shared/contexts/authContext";
import {REFRESH_TYPE_FUNCTION} from "../../shared/utils/enum";
import * as Diff from "diff";
import Papa from 'papaparse';
import {Rnd} from 'react-rnd';
import {internalApi} from "../../common/axios";
import {buildUrl} from "../../shared/utils/net";
import {useReloadTime} from "../../features/serverInfo/useServerInfo";

const {Title, Text} = Typography;
const {TextArea} = Input;

// CSV 버전 목록과 무관한 공용 메모가 쓰는 csv_memo.version 값
const MAIN_MEMO_VERSION = '__main__';

const onlyCsv = (files) => files.filter(f => f.name.toLowerCase().endsWith('.csv'));

// 드롭된 파일/폴더 entry에서 File 목록을 재귀로 수집
const readEntryFiles = async (entry) => {
    if (entry.isFile) return [await new Promise((resolve, reject) => entry.file(resolve, reject))];
    const reader = entry.createReader();
    const files = [];
    let batch;
    do { // readEntries는 한 번에 최대 100개만 돌려주므로 빈 배열이 올 때까지 반복
        batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        for (const child of batch) files.push(...await readEntryFiles(child));
    } while (batch.length);
    return files;
};

const CSV = () => {
    const {darkMode} = useContext(DarkModeContext);
    const {currentUser} = useContext(AuthContext);
    const [version, setVersion] = useState('');
    const [branch, setBranch] = useState('');
    const [selectedVersions, setSelectedVersions] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [modalSize, setModalSize] = useState({width: 1400, height: 800});
    const [modalPosition, setModalPosition] = useState({x: window.innerWidth / 2 - 700, y: 100});

    const {data: csvList, isLoading, error} = useCSVList();
    const {data: memoMap} = useCsvMemo();
    const {mutate: uploadCSV, isPending} = useUploadCSV();
    const {mutate: deleteCSV, isPending: isDeleting} = useDeleteCSV();
    // TODO: GitLab 업로드 추후 삭제 예정 (useUploadCSV, useGitlabBranchList)
    const {data: branchList, isLoading: isLoadingBranches, error: errorGitBranch} = useGitlabBranchList();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();

    const isDevelopTarget = currentUser?.target === 'develop';
    const isDockerTarget = currentUser?.target === 'docker';

    // TODO: GitLab 업로드 탭 추후 삭제 예정 → 삭제 시 uploadMode/Segmented 제거하고 로컬 업로드만 남김
    const [uploadMode, setUploadMode] = useState('local');
    // docker는 GitLab 연동이 없어 로컬 업로드만 가능
    const isLocalUpload = isDockerTarget || uploadMode === 'local';
    const [localFiles, setLocalFiles] = useState([]);

    const handleFolderDrop = async (e) => {
        e.preventDefault();
        // dataTransfer는 이벤트가 끝나면 비워지므로 await 전에 entry를 먼저 꺼내둔다
        const entries = Array.from(e.dataTransfer.items).map(item => item.webkitGetAsEntry?.()).filter(Boolean);
        const files = (await Promise.all(entries.map(readEntryFiles))).flat();
        setLocalFiles(onlyCsv(files));
    };
    const {mutate: uploadCSVLocal, isPending: isPendingLocal} = useUploadCSVLocal();

    // Modal 관련 상태
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSelectedVersions, setModalSelectedVersions] = useState([]);
    const [modalCsvContents, setModalCsvContents] = useState([]); // [[{filename, content}], [{filename, content}]]
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState('');
    const [modalDiff, setModalDiff] = useState("");
    const [diffFileList, setDiffFileList] = useState([]);
    const [selectPlaceholder, setSelectPlaceholder] = useState('브랜치 선택');

    const handleMemoSave = (version, memo) => {
        internalApi.post('manager/csv/memo', {version, memo}).catch(() => toast.error('메모 저장 실패'));
    }

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const handleDownload = async (versions) => {
        if (versions.length === 0) return;
        toast.loading('CSV 다운로드를 시작합니다...');
        try {
            const {data} = await internalApi.get(buildUrl('manager/csv/download', {versions: versions.join(',')}), {
                responseType: 'blob',
                skipErrorToast: true // 에러 본문도 blob 이라 인터셉터 토스트가 깨짐
            });
            const zipName = versions.length === 1
                ? versions[0]
                : `csv_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')}`;
            downloadBlob(data, `${zipName}.zip`);
            toast.dismiss();
        } catch (e) {
            toast.dismiss();
            toast.error('다운로드 실패');
        }
    }

    // 모달에 이미 내용이 로드되어 있으므로 서버 호출 없이 저장
    const handleFileDownload = (versionIdx, filename) => {
        const content = modalCsvContents[versionIdx]?.find(f => f.filename === filename)?.content;
        if (content == null) return;
        downloadBlob(new Blob(['\ufeff' + content], {type: 'text/csv;charset=utf-8;'}), filename);
    }

    const handleUploadCSV = async () => {
        if (isLocalUpload) {
            if (!version.trim() || !localFiles.length) {
                toast.error('버전과 CSV 파일(폴더)을 모두 선택하세요.');
                return;
            }
            await uploadCSVLocal({version, files: localFiles}, {
                onSuccess: () => {
                    toast.dismiss();
                    toast.success("업로드가 완료 되었습니다.");
                    setVersion('');
                    setLocalFiles([]);
                },
                onError: () => {
                    toast.dismiss();
                    toast.error("업로드에 실패했습니다. 다시 시도해 주세요.");
                }
            });
            return;
        }

        // TODO: GitLab 업로드 추후 삭제 예정
        if (!version.trim() || !branch.trim()) {
            toast.error('버전 정보와 브랜치를 모두 입력하세요.');
            return;
        }

        await uploadCSV({version, branch}, {
            onSuccess: () => {
                toast.dismiss();
                toast.success("업로드가 완료 되었습니다.");
                setVersion('');
            },
            onError: () => {
                toast.dismiss();
                toast.error("업로드에 실패했습니다. 다시 시도해 주세요.");
            }
        });
    };

    const handleToggleSelectVersion = (version) => {
        if (isLoading || isPending || isDeleting) return;
        setSelectedVersions(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(version)) {
                newSelected.delete(version);
            } else {
                newSelected.add(version);
            }
            return newSelected;
        });
    };

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedVersions(new Set(csvList));
        } else {
            setSelectedVersions(new Set());
        }
    };

    const handleDeleteVersion = async (versionToDelete) => {
        await deleteCSV(versionToDelete, {
            onSuccess: () => {
                toast.dismiss();
                toast.success("삭제가 완료 되었습니다.");
                setSelectedVersions(prevSelected => {
                    const newSelected = new Set(prevSelected);
                    newSelected.delete(versionToDelete);
                    return newSelected;
                });
            },
            onError: () => {
                toast.dismiss();
                toast.error("삭제에 실패했습니다. 다시 시도해 주세요.");
            }
        });
    };

    const handleDeleteSelectedVersions = async () => {
        if (selectedVersions.size === 0) {
            toast.error('삭제할 버전을 선택하세요.');
            return;
        }

        const versionsToDelete = Array.from(selectedVersions);
        await deleteCSV(versionsToDelete, {
            onSuccess: () => {
                toast.dismiss();
                toast.success("선택한 버전들이 삭제되었습니다.");
                setSelectedVersions(new Set());
                setSelectAll(false);
            },
            onError: () => {
                toast.dismiss();
                toast.error("선택한 버전 삭제에 실패했습니다. 다시 시도해 주세요.");
            }
        });
    };

    // 여러 파일을 받아오는 fetch 함수
    const fetchCsvContent = async (version) => {
        try {
            setModalLoading(true);
            const {data} = await internalApi(`manager/csv/${version}`);
            const arr = data.rows;
            setModalLoading(false);
            return arr;
        } catch (e) {
            setModalLoading(false);
            return [];
        }
    };

    // Modal 열기
    // handleOpenModal 내부에서 diff 파일만 추리기
    const handleOpenModal = async () => {
        toast.loading('CSV 데이터를 불러오는 중입니다...');
        const versions = Array.from(selectedVersions);
        if (versions.length === 0) return;
        setModalSelectedVersions(versions);
        setModalLoading(true);
        let contents = [];
        for (let v of versions) {
            contents.push(await fetchCsvContent(v));
        }
        toast.dismiss();
        setModalCsvContents(contents);
        setModalLoading(false);

        // 변경된 파일만 추리기 (2개 선택 시)
        let diffFiles = [];
        if (versions.length === 2) {
            const filesA = contents[0] || [];
            const filesB = contents[1] || [];
            const fileNames = Array.from(new Set([
                ...filesA.map(f => f.filename),
                ...filesB.map(f => f.filename)
            ]));
            diffFiles = fileNames.filter(name => {
                const a = filesA.find(f => f.filename === name)?.content || '';
                const b = filesB.find(f => f.filename === name)?.content || '';
                return a !== b;
            });
            // diffFiles가 비어있으면 전체 보기로
            setSelectedFile(diffFiles[0] || '');
        } else {
            // 1개 선택 시 기존대로
            setSelectedFile(contents[0]?.[0]?.filename || '');
        }
        setIsModalOpen(true);

        // 변경된 파일만 리스트에 반영하려면 상태 추가 필요
        setDiffFileList(diffFiles); // diffFileList: useState([])로 선언 필요
    };


    // 파일 선택 시 diff 갱신
    const handleSelectFile = (filename) => {
        setSelectedFile(filename);
        if (modalSelectedVersions.length === 2) {
            const fileA = modalCsvContents[0].find(f => f.filename === filename);
            const fileB = modalCsvContents[1].find(f => f.filename === filename);
            const diff = Diff.createPatch(
                `${filename} (${modalSelectedVersions[0]} vs ${modalSelectedVersions[1]})`,
                fileA?.content || '',
                fileB?.content || ''
            );
            setModalDiff(diff);
        }
    };

    // Modal 닫기
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalSelectedVersions([]);
        setModalCsvContents([]);
        setModalDiff("");
        setSelectedFile('');
    };

    const renderCsvTable = (csvString) => {
        if (!csvString) return null;
        const {data} = Papa.parse(csvString.trim(), {skipEmptyLines: true});
        if (!data || data.length === 0) return null;
        const columns = data[0].map((col, idx) => ({
            title: col,
            dataIndex: `col${idx}`,
            key: `col${idx}`,
            render: (text) => <span style={{whiteSpace: 'pre-wrap'}}>{text}</span>
        }));
        const dataSource = data.slice(1).map((row, ridx) => {
            const obj = {key: ridx};
            row.forEach((cell, cidx) => {
                obj[`col${cidx}`] = cell;
            });
            return obj;
        });
        return <Table columns={columns} dataSource={dataSource} size="small" bordered pagination={false}
                      style={{marginBottom: 24}}/>;
    };

    // CSV Diff 시각화 함수
    const renderCsvDiff = (oldStr, newStr) => {
        const diff = Diff.diffLines(oldStr || '', newStr || '');
        return (
            <div style={{
                fontFamily: 'monospace',
                fontSize: 13,
                background: '#f6f6f6',
                padding: 12,
                borderRadius: 8,
                overflowX: 'auto'
            }}>
                {diff.filter(part => part.added || part.removed).map((part, idx) => {
                    let color = '#222';
                    let bg = 'none';
                    if (part.added) {
                        color = '#237804';
                        bg = '#f6ffed';
                    } else if (part.removed) {
                        color = '#a8071a';
                        bg = '#fff1f0';
                    }
                    return (
                        <span key={idx} style={{background: bg, color, display: 'block', whiteSpace: 'pre'}}>
                            {part.added ? '+ ' : part.removed ? '- ' : '  '}{part.value}
                        </span>
                    );
                })}
            </div>
        );
    };

    if (isLoading || (isLoadingBranches && !isDevelopTarget && !isDockerTarget) || isReloadTimeData) {
        return <IsLoading/>
    }

    const firstError = error || (!isDevelopTarget && !isDockerTarget ? errorGitBranch : null) || reloadTimeDataError;
    if (firstError) {
        return <ErrorPage error={firstError}/>;
    }

    const anyPending = isPending || isPendingLocal;

    if (isDeleting) {
        toast.loading("삭제 중입니다. 잠시만 기다려 주세요.");
    }
    if (anyPending) {
        toast.loading("업로드 중입니다. 잠시만 기다려 주세요.");
    }

    return (
        <div className={`csv-container ${darkMode ? 'dark-theme' : 'light-theme'}`}>
            <div className="csv-header">
                <Space align="center" size="middle">
                    <div className="header-icon">
                        <FileTextOutlined/>
                    </div>
                    <div>
                        <Title level={3} style={{margin: 0, color: 'var(--text-primary)'}}>
                            CSV 관리
                        </Title>
                        <Text type="secondary" style={{fontSize: '14px'}}>
                            CSV 파일을 업로드하고 관리하세요
                        </Text>
                    </div>
                </Space>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16}}>
                    {/* 버전과 무관한 공용 메모. csv_memo 테이블의 version='__main__' 행을 재사용한다. */}
                    <TextArea
                        key={`memo-main-${memoMap?.[MAIN_MEMO_VERSION] ?? ''}`}
                        defaultValue={memoMap?.[MAIN_MEMO_VERSION] || ''}
                        placeholder="메모"
                        autoSize={{minRows: 2, maxRows: 6}}
                        style={{flex: 1, maxWidth: 640}}
                        onBlur={(e) => handleMemoSave(MAIN_MEMO_VERSION, e.target.value)}
                    />
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                        <ServerRefresh refreshTypeFunc={REFRESH_TYPE_FUNCTION.SERVER_INFO} reloadTimeRefetch={reloadTimeDataRefetch}/>
                        <Text type="secondary" style={{fontSize: '14px'}}>
                            마지막 동기화 (서버정보, CSV) : {reloadTimeData.admin_table_sync_date ? reloadTimeData.admin_table_sync_date + ' (KST)' : '정보 없음'}
                        </Text>
                    </div>
                </div>
            </div>

            <Row gutter={[24, 24]}>
                {/* 업로드 섹션 */}
                <Col xs={24} lg={10}>
                    <Card
                        className="upload-card"
                        title={
                            <Space>
                                <UploadOutlined style={{color: '#52c41a'}}/>
                                <span>CSV 업로드</span>
                            </Space>
                        }
                        style={{
                            background: 'var(--card-background)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            boxShadow: 'var(--card-shadow)'
                        }}
                    >
                        <Space direction="vertical" style={{width: '100%'}} size="large">
                            <div className="upload-input-section">
                                <Text strong style={{display: 'block', marginBottom: 8, color: 'var(--text-primary)'}}>
                                    버전 정보
                                </Text>
                                {/* TODO: GitLab 탭 추후 삭제 예정 */}
                                {!isDockerTarget && (
                                    <Segmented
                                        block
                                        value={uploadMode}
                                        onChange={setUploadMode}
                                        options={[{label: '로컬 폴더', value: 'local'}, {label: 'GitLab', value: 'git'}]}
                                        style={{marginBottom: 8}}
                                    />
                                )}
                                {isLocalUpload ? (
                                    <>
                                        <Input
                                            value={version}
                                            onChange={(e) => setVersion(e.target.value)}
                                            placeholder="버전을 입력하세요 (예: 1.0.0)"
                                            style={{marginBottom: 8}}
                                        />
                                        <input
                                            type="file"
                                            id="csv-folder-input"
                                            webkitdirectory="true"
                                            multiple
                                            style={{display: 'none'}}
                                            onChange={(e) => setLocalFiles(onlyCsv(Array.from(e.target.files)))}
                                        />
                                        <div
                                            onClick={() => document.getElementById('csv-folder-input').click()}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={handleFolderDrop}
                                            style={{
                                                padding: '24px 12px',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: '1px dashed var(--border-color)',
                                                borderRadius: 8,
                                                color: 'var(--text-primary)'
                                            }}
                                        >
                                            <InboxOutlined style={{fontSize: 32, color: '#1677ff'}}/>
                                            <div style={{marginTop: 8}}>
                                                {localFiles.length > 0
                                                    ? `${localFiles.length}개 CSV 파일 선택됨`
                                                    : 'CSV 폴더를 끌어다 놓거나 클릭해서 선택'}
                                            </div>
                                        </div>
                                        {localFiles.length > 0 && (
                                            <List
                                                size="small"
                                                bordered
                                                dataSource={localFiles}
                                                renderItem={file => <List.Item>{file.webkitRelativePath || file.name}</List.Item>}
                                                style={{marginTop: 8, maxHeight: 240, overflowY: 'auto', borderRadius: 8}}
                                            />
                                        )}
                                    </>
                                ) : (
                                    // TODO: GitLab 업로드 UI 추후 삭제 예정
                                    <>
                                        <Select
                                            showSearch
                                            allowClear={!isDevelopTarget}
                                            placeholder={selectPlaceholder}
                                            options={(branchList || []).map((b) => ({value: b, label: b}))}
                                            loading={isDevelopTarget ? false : isLoadingBranches}
                                            onChange={(value) => setBranch(value || '')}
                                            value={branch || undefined}
                                            style={{width: '100%', marginBottom: 8}}
                                            optionFilterProp="label"
                                            onFocus={() => setSelectPlaceholder('브랜치를 검색하세요...')}
                                            onBlur={() => setSelectPlaceholder('브랜치 선택')}
                                        />
                                        <TextArea
                                            value={version}
                                            onChange={(e) => setVersion(e.target.value)}
                                            placeholder="업로드할 버전 정보를 입력하세요..."
                                            rows={6}
                                            className="version-input"
                                            style={{
                                                background: 'var(--card-background)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                color: 'var(--text-primary)'
                                            }}
                                        />
                                    </>
                                )}
                            </div>

                            <Button
                                type="primary"
                                size="large"
                                icon={<UploadOutlined/>}
                                onClick={handleUploadCSV}
                                disabled={isLoading || anyPending || isDeleting ||
                                    (isLocalUpload ? !version.trim() || !localFiles.length : !version.trim() || !branch.trim())}
                                loading={anyPending}
                                className="upload-button"
                                style={{
                                    width: '100%',
                                    height: '48px',
                                    borderRadius: '8px',
                                    fontWeight: 600
                                }}
                            >
                                {anyPending ? '업로드 중...' : 'CSV 업로드'}
                            </Button>
                        </Space>
                    </Card>
                </Col>

                {/* 버전 리스트 섹션 */}
                <Col xs={24} lg={14}>
                    <Card
                        className="version-list-card"
                        title={
                            <Space>
                                <InboxOutlined style={{color: '#1890ff'}}/>
                                <span>버전 리스트</span>
                                <Badge count={csvList?.length || 0} style={{backgroundColor: '#52c41a'}}/>
                            </Space>
                        }
                        extra={
                            csvList?.length > 0 && (
                                <Space>
                                    <Checkbox
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        disabled={isLoading || isPending || isDeleting}
                                    >
                                        전체 선택
                                    </Checkbox>
                                    <Popconfirm
                                        title="선택된 버전들을 삭제하시겠습니까?"
                                        description={`${selectedVersions.size}개 항목이 삭제됩니다.`}
                                        onConfirm={handleDeleteSelectedVersions}
                                        disabled={selectedVersions.size === 0}
                                        okText="삭제"
                                        cancelText="취소"
                                        icon={<ExclamationCircleOutlined style={{color: 'red'}}/>}
                                    >
                                        <Button
                                            danger
                                            icon={<DeleteOutlined/>}
                                            disabled={selectedVersions.size === 0 || isLoading || isPending || isDeleting}
                                            loading={isDeleting}
                                        >
                                            선택 삭제 ({selectedVersions.size})
                                        </Button>
                                    </Popconfirm>
                                    <Button
                                        icon={<DownloadOutlined/>}
                                        disabled={selectedVersions.size === 0 || isLoading || isPending || isDeleting}
                                        onClick={() => handleDownload(Array.from(selectedVersions))}
                                    >
                                        선택 다운로드 ({selectedVersions.size})
                                    </Button>
                                    <Button
                                        type="default"
                                        icon={<FileTextOutlined/>}
                                        disabled={selectedVersions.size === 0 || selectedVersions.size > 2 || isLoading || isPending || isDeleting || modalLoading}
                                        onClick={handleOpenModal}
                                    >
                                        {selectedVersions.size === 1 ? "CSV 보기" : "Diff 보기"}
                                    </Button>
                                </Space>
                            )
                        }
                        style={{
                            background: 'var(--card-background)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            boxShadow: 'var(--card-shadow)'
                        }}
                    >
                        {csvList?.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Text type="secondary">
                                        업로드된 CSV 버전이 없습니다
                                    </Text>
                                }
                                style={{padding: '40px 0'}}
                            />
                        ) : (
                            <List
                                className="version-list"
                                dataSource={csvList}
                                renderItem={(item) => (
                                    <List.Item
                                        className={`version-item ${selectedVersions.has(item) ? 'selected' : ''}`}
                                        onClick={() => handleToggleSelectVersion(item)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            border: '1px solid var(--border-color)',
                                            background: selectedVersions.has(item)
                                                ? 'rgba(24, 144, 255, 0.1)'
                                                : 'var(--card-background)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        actions={[
                                            <Tooltip title="다운로드">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DownloadOutlined/>}
                                                    disabled={isLoading || isPending || isDeleting}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownload([item]);
                                                    }}
                                                    style={{borderRadius: '6px'}}
                                                />
                                            </Tooltip>,
                                            <Tooltip title="삭제">
                                                <Popconfirm
                                                    title="이 버전을 삭제하시겠습니까?"
                                                    onConfirm={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteVersion(item);
                                                    }}
                                                    okText="삭제"
                                                    cancelText="취소"
                                                    icon={<ExclamationCircleOutlined style={{color: 'red'}}/>}
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined/>}
                                                        disabled={isLoading || isPending || isDeleting}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{borderRadius: '6px'}}
                                                    />
                                                </Popconfirm>
                                            </Tooltip>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Checkbox
                                                    checked={selectedVersions.has(item)}
                                                    onChange={() => handleToggleSelectVersion(item)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                            title={
                                                <Space>
                                                    {selectedVersions.has(item) && (
                                                        <CheckCircleOutlined style={{color: '#52c41a'}}/>
                                                    )}
                                                    <Text strong style={{color: 'var(--text-primary)'}}>
                                                        {item}
                                                    </Text>
                                                </Space>
                                            }
                                            description={
                                                <Input
                                                    key={`memo-${item}`}
                                                    defaultValue={memoMap?.[item] || ''}
                                                    placeholder="메모"
                                                    size="small"
                                                    style={{width: '100%', maxWidth: 400}}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onBlur={(e) => handleMemoSave(item, e.target.value)}
                                                    onPressEnter={(e) => handleMemoSave(item, e.target.value)}
                                                />
                                            }
                                        />
                                    </List.Item>
                                )}
                                style={{overflowY: 'auto'}}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
            {/* Modal 구현 (Antd Modal 제거, 직접 오버레이+Rnd로 구현) */}
            {isModalOpen && (
                <>
                    {/* 오버레이 */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            background: 'rgba(0,0,0,0.35)',
                            zIndex: 1000
                        }}
                        onClick={handleCloseModal}
                    />
                    {/* Rnd 모달 */}
                    <Rnd
                        size={modalSize}
                        position={modalPosition}
                        minWidth={800}
                        minHeight={400}
                        maxWidth={2400}
                        maxHeight={window.innerHeight - 40}
                        bounds="window"
                        onDragStop={(e, d) => setModalPosition({x: d.x, y: d.y})}
                        onResizeStop={(e, direction, ref, delta, position) => {
                            setModalSize({width: parseInt(ref.style.width), height: parseInt(ref.style.height)});
                            setModalPosition(position);
                        }}
                        style={{zIndex: 1100}}
                        dragHandleClassName="csv-modal-title"
                    >
                        <div style={{
                            background: '#fff',
                            borderRadius: 8,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }} onClick={e => e.stopPropagation()}>
                            <div className="csv-modal-title" style={{
                                cursor: 'move',
                                fontWeight: 600,
                                fontSize: 18,
                                padding: '16px 24px',
                                borderBottom: '1px solid #eee',
                                background: '#fafafa',
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span>{modalSelectedVersions.length === 2 ? "CSV Diff 보기" : "CSV 내용 보기"}</span>
                                <Button type="text" onClick={handleCloseModal} style={{fontSize: 18}}>&times;</Button>
                            </div>
                            <div style={{flex: 1, padding: 24, overflow: 'hidden', height: 0, minHeight: 0}}>
                                <Row gutter={16} style={{height: '100%'}}>
                                    <Col span={6} style={{height: '100%'}}>
                                        <div style={{
                                            height: '100%',
                                            overflowY: 'auto',
                                            borderRight: '1px solid #eee',
                                            paddingRight: 8
                                        }}>
                                            <List
                                                dataSource={
                                                    modalSelectedVersions.length === 2
                                                        ? diffFileList
                                                        : (modalCsvContents[0]?.map(f => f.filename) || [])
                                                }
                                                renderItem={(item, idx) => (
                                                    <List.Item
                                                        style={{
                                                            cursor: 'pointer',
                                                            background: selectedFile === item ? '#e6f7ff' : undefined
                                                        }}
                                                        onClick={() => handleSelectFile(item)}
                                                    >
                                                        <Text strong>{item === 'ALL_FILES' ? '전체 보기' : item}</Text>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={18} style={{height: '100%'}}>
                                        <div style={{height: '100%', overflowY: 'auto', paddingLeft: 8}}>
                                            {selectedFile && selectedFile !== 'ALL_FILES' && (
                                                <Space style={{marginBottom: 8}}>
                                                    {modalSelectedVersions.map((v, idx) => (
                                                        <Button
                                                            key={v}
                                                            size="small"
                                                            icon={<DownloadOutlined/>}
                                                            onClick={() => handleFileDownload(idx, selectedFile)}
                                                        >
                                                            {modalSelectedVersions.length === 1 ? '다운로드' : v}
                                                        </Button>
                                                    ))}
                                                </Space>
                                            )}
                                            {modalLoading ? (
                                                <Spin/>
                                            ) : modalSelectedVersions.length === 2 ? (
                                                (() => {
                                                    const fileA = modalCsvContents[0]?.find(f => f.filename === selectedFile);
                                                    const fileB = modalCsvContents[1]?.find(f => f.filename === selectedFile);
                                                    return renderCsvDiff(fileA?.content || '', fileB?.content || '');
                                                })()
                                            ) : (
                                                selectedFile === 'ALL_FILES'
                                                    ? (modalCsvContents[0]?.map(f => (
                                                        <div key={f.filename}>
                                                            <Text strong>{`--- ${f.filename} ---`}</Text>
                                                            {renderCsvTable(f.content)}
                                                        </div>
                                                    )) || null)
                                                    : renderCsvTable(modalCsvContents[0]?.find(f => f.filename === selectedFile)?.content)
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div style={{
                                padding: 12,
                                borderTop: '1px solid #eee',
                                background: '#fafafa',
                                borderBottomLeftRadius: 8,
                                borderBottomRightRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 16,
                                justifyContent: 'flex-end'
                            }}>
                                <span style={{fontSize: 13}}>가로 사이즈(px):</span>
                                <Slider
                                    min={800}
                                    max={2400}
                                    step={20}
                                    value={modalSize.width}
                                    onChange={w => setModalSize(s => ({...s, width: w}))}
                                    style={{width: 200}}
                                />
                                <InputNumber
                                    min={800}
                                    max={2400}
                                    step={20}
                                    value={modalSize.width}
                                    onChange={w => setModalSize(s => ({...s, width: w}))}
                                />
                            </div>
                        </div>
                    </Rnd>
                </>
            )}
            <ScrollToTop/>
        </div>
    );
};

export default CSV;

